import { APP_EMITTER_EVENTS, AppEmitter, FormLabel, OptionType, SearchableSelect } from "shared";
import { AppLang } from "app";
import { useTranslation } from "react-i18next";
import { ITemplateInputWithSelectComponent } from "entities/template";
import React, { useEffect, useMemo, useState } from "react";
import { IValidationFunc } from "../../../../lib/types/validation";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_input_with_select.module.scss";

interface IDocumentFormInputWithSelectProps {
    component: ITemplateInputWithSelectComponent;
    enLanguageEnabled: boolean;
    inputWithSelect: boolean;
    externalUpdateRecord: Record<string, any>;
    onChange: (tag: string, value?: string) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    triggerValidate: number;
    labelShort: boolean;
}

export const DocumentFormInputWithSelect: React.FC<IDocumentFormInputWithSelectProps> = React.memo(({
                                                                                                        component,
                                                                                                        enLanguageEnabled,
                                                                                                        inputWithSelect,
                                                                                                        triggerValidate,
                                                                                                        externalUpdateRecord,
                                                                                                        label,
                                                                                                        labelShort,
                                                                                                        readonly,
                                                                                                        tooltip,
                                                                                                        onValidationRegister,
                                                                                                        onChange,
                                                                                                    }) => {
    const [value, setValue] = useState<string | undefined>();
    const { i18n } = useTranslation();

    const required = useMemo(() => {
        return component.required !== false;
    }, [component]);

    useEffect(() => {
        return () => {
            onValidationRegister(() => {
                return Promise.resolve(true);
            }, component.name);
            onChange(component.name, undefined);
        };
    }, []);

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    useEffect(() => {
        setTimeout(() => {
            if (component.defaultValue && component.list && Array.isArray(component.list) && component.list.includes(component.defaultValue)) {
                const externalValue = externalUpdateRecord[component.name];
                if (!externalValue) {
                    setValue(component.defaultValue);
                }
            }
        }, 100);
    }, []);

    useEffect(() => {
        if (component.name) {
            const selectValue = externalUpdateRecord[component.name];
            setValue(selectValue);
        }
    }, [externalUpdateRecord, component.name]);

    useEffect(() => {
        if (component.name) {
            onValidationRegister(validateInput, component.name);
            onChange(component.name, value);
        }
    }, [component.name, value, required]);

    const handleClearEvent = () => {
        if (!readonly) {
            setValue(undefined);
        }
    };

    const options: OptionType[] = useMemo(() => {
        const ruList = component.list;
        const enList = enLanguageEnabled && component.list_en ? component.list_en : ruList;
        const selectList = i18n.language === AppLang.RU ? ruList : enList;
        return selectList && selectList.length > 0 && ruList && ruList.length > 0 ? selectList.map((o, index) => {
            return {
                value: ruList[index],
                label: o
            };
        }) : [];

    }, [component, i18n.language]);

    const validateInput = (): Promise<boolean> => {
        const valid = !required || !!value;
        return Promise.resolve(valid);
    };

    const getOptionValue = (val: string | undefined) => {
        if (!val) {
            return undefined;
        }
        const optValue = options.find(o => o.value === value);
        return optValue ? optValue : {
            value: val,
            label: val
        };
    };

    return component.name ? <FormLabel title={label} required={required} disabled={readonly} labelShort={labelShort}>
        <div className={styles.container}>
            <SearchableSelect options={options}
                              isCreatable={inputWithSelect && !component.selectonly}
                              className={styles.input}
                              validate={required}
                              triggerValidate={triggerValidate}
                              optionValue={getOptionValue(value)}
                              onChange={(val) => {
                                  setValue(val ? val as string : undefined);
                              }}
                              isMulti={false}
                              disabled={readonly}
                              hideClearIcon={required}
                              isSearchable={true}/>
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltip}/>
        </div>
    </FormLabel> : null;
});
